import { useNavigate } from 'react-router-dom';
import { RoutePaths, mapTaskStatusToText } from '../../../../../../constants';
import { PatientDetailsMenu } from '../../../../PatientDetailsMenu/PatientDetailsMenu';
import styles from './PatientDetails.module.scss';
import cx from 'classnames';
import { Icon, RolePermissionChecker, Text } from 'components';
import { PatientTask } from 'components/PatientList/components/PatientTask/PatientTask';
import { useDevice } from 'hooks';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCaseClinic } from 'store/patients/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { TaskBlockReason, TreatmentPlanTask, UserRole, taskBlockReasonsText } from 'types';
import { calculateAge } from 'utils';
import { getFormattedDate } from 'utils/getFormattedData';

type Props = {
  id: number;
  firstName: string;
  lastName: string;
  taskStatusUpdatedAt: string;
  oralHealthTherapist: string;
  task: TreatmentPlanTask;
  submittedAt: string;
  isArchived: boolean;
  isOutOfTreatment: boolean;
  outOfTreatmentReason: TaskBlockReason;
  outOfTreatmentCustomReason: string | null;
  externalPatientId: number;
  dateOfBirth: string;
};

export const PatientDetails = ({
  firstName,
  lastName,
  taskStatusUpdatedAt,
  id,
  oralHealthTherapist,
  task,
  submittedAt: submittedTimestamp,
  isArchived,
  isOutOfTreatment,
  outOfTreatmentReason,
  outOfTreatmentCustomReason,
  externalPatientId,
  dateOfBirth,
}: Props) => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { isTablet } = useDevice();

  const updatedAt = getFormattedDate(taskStatusUpdatedAt);
  const submittedAt = getFormattedDate(submittedTimestamp);

  const patientCaseClinic = useSelector(selectCurrentPatientCaseClinic);

  const rolesAllowedToEdit = isArchived ? [UserRole.ADMIN] : [UserRole.CLINIC, UserRole.ADMIN];

  const openTasksModalOrNavigateToTasksHandler = () => {
    isTablet
      ? navigate(`${RoutePaths.TASKS}/${id}`)
      : dispatch(appActions.setIsModalOpened({ name: 'tasksModal', isOpened: true }));
  };
  const age = `${calculateAge(dateOfBirth)} y.o.`;
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={cx(styles.patientInfo, { [styles.isArchived]: isArchived })}>
          <div className={styles.patientNameControl}>
            <div>
              <Text type="h5">{`${firstName} ${lastName}`}</Text>
              <Text type="caption" className={styles.age}>
                {age}
              </Text>
            </div>
            {!isTablet && (
              <RolePermissionChecker rolesAllowedToView={rolesAllowedToEdit}>
                <PatientDetailsMenu />
              </RolePermissionChecker>
            )}
          </div>
          {isArchived && (
            <div className={styles.archivedLabel}>
              <PatientTask task="ARCHIVED" />
            </div>
          )}
          <div className={styles.infoChips}>
            <div className={styles.infoChip}>
              <Icon name="Card" className={styles.chipIcon} />
              <Text type="caption">{externalPatientId}</Text>
            </div>
            <div className={styles.infoChip}>
              <Icon name="Location" className={styles.chipIcon} />
              <Text type="caption">{patientCaseClinic}</Text>
            </div>
            <div className={styles.infoChip}>
              <Icon name="Diagnosis" className={styles.chipIcon} />
              <Text type="caption">{oralHealthTherapist}</Text>
            </div>
          </div>
          {submittedTimestamp && (
            <div className={styles.submittedDate}>
              <Text type="caption">Patient case submitted: </Text>
              <Text type="caption">{submittedAt}</Text>
            </div>
          )}
        </div>
      </div>
      <div className={styles.progress}>
        <div
          className={cx(styles.progressSteps, styles[task.toLowerCase()], {
            [styles.isOutOfTreatment]: isOutOfTreatment,
          })}>
          <div className={styles.step}>
            <div className={styles.stepDot} />
            <div className={styles.stepDescription}>
              <Icon name="DiagnosisLarge" className={styles.stepIcon} />
              <Text type="caption" className={styles.stepName}>
                New patient
              </Text>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepDot} />
            <div className={styles.stepDescription}>
              <Icon name="Letter" className={styles.stepIcon} />
              <Text type="caption" className={styles.stepName}>
                Letter
              </Text>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepDot} />
            <div className={styles.stepDescription}>
              <Icon name="inTreatment" className={styles.stepIcon} />
              <Text type="caption" className={styles.stepName}>
                Treatment
              </Text>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepDot} />
            <div className={styles.stepDescription}>
              <Icon name="Finished" className={styles.stepIcon} />
              <Text type="caption" className={styles.stepName}>
                Finished
              </Text>
            </div>
          </div>
        </div>
        <div className={styles.currentStatusContainer}>
          <div
            className={cx(styles.currentStatusChip, styles[task.toLowerCase()], {
              [styles.isOutOfTreatment]: isOutOfTreatment,
            })}
            onClick={openTasksModalOrNavigateToTasksHandler}>
            <div className={styles.currentStatusTextContainer}>
              <Text type="overline" className={styles.currentStatusTextTitle}>
                {isOutOfTreatment ? `Out of treatment (From ${mapTaskStatusToText[task]})` : mapTaskStatusToText[task]}
              </Text>
              {outOfTreatmentReason && isOutOfTreatment && (
                <Text
                  type="caption"
                  className={
                    outOfTreatmentReason === TaskBlockReason.OTHER
                      ? styles.currentStatusTextDescriptionCustom
                      : styles.currentStatusTextDescription
                  }>
                  {outOfTreatmentReason === TaskBlockReason.OTHER
                    ? outOfTreatmentCustomReason
                    : taskBlockReasonsText[outOfTreatmentReason]}
                </Text>
              )}
            </div>
            <Icon name="ArrowRight" />
          </div>
          <div className={styles.currentStatusTimestamp}>
            <Icon name="LastUpdate" />
            <Text type="caption">{updatedAt}</Text>
          </div>
        </div>
      </div>
    </div>
  );
};
